import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container,
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Stack,
  Paper
} from '@mui/material';
import {
  CheckCircle,
  Error,
  Email,
  Home,
  Refresh
} from '@mui/icons-material';
import user_api from '../../../api/user';

const VerifyEmail = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [message, setMessage] = useState('');
  const [resending, setResending] = useState(false);

  useEffect(() => {
    const verifyEmail = async () => {
      const token = searchParams.get('token');
      
      if (!token) {
        setStatus('error');
        setMessage('Invalid verification link. Please request a new verification email.');
        return;
      }

      try {
        const response = await user_api.verify_email(token);
        
        if (response && response.data && response.data.success) {
          setStatus('success');
          setMessage('Your email has been successfully verified!');
        } else {
          setStatus('error');
          setMessage(response?.data?.message || 'Email verification failed. Please try again or request a new verification email.');
        }
      } catch (error) {
        setStatus('error');
        setMessage('An error occurred during verification. Please try again or request a new verification email.');
      }
    };

    verifyEmail();
  }, [searchParams]);

  const handleResendVerification = async () => {
    setResending(true);
    try {
      const response = await user_api.resend_verification_email();
      
      if (response && response.data && response.data.success) {
        setMessage('A new verification email has been sent to your email address.');
      } else {
        setMessage(response?.data?.message || 'Failed to send verification email. Please try again later.');
      }
    } catch (error) {
      setMessage('An error occurred. Please try again later.');
    } finally {
      setResending(false);
    }
  };

  const rootStyle = {
    minHeight: '100vh',
    background: 'linear-gradient(to bottom right, #0f172a, #1e293b)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px'
  };

  const cardStyle = {
    maxWidth: 600,
    width: '100%',
    background: 'rgba(15, 23, 42, 0.8)',
    backdropFilter: 'blur(10px)',
    border: '1px solid rgba(100, 116, 139, 0.2)',
    boxShadow: '0 4px 24px 0 rgba(0, 0, 0, 0.1)',
  };

  const getIcon = () => {
    switch (status) {
      case 'verifying':
        return <CircularProgress size={60} sx={{ color: '#00F5FF' }} />;
      case 'success':
        return <CheckCircle sx={{ fontSize: 60, color: '#4CAF50' }} />;
      case 'error':
        return <Error sx={{ fontSize: 60, color: '#F44336' }} />;
      default:
        return <Email sx={{ fontSize: 60, color: '#00F5FF' }} />;
    }
  };

  const getTitle = () => {
    switch (status) {
      case 'verifying':
        return 'Verifying Your Email...';
      case 'success':
        return 'Email Verified!';
      case 'error':
        return 'Verification Failed';
      default:
        return 'Email Verification';
    }
  };

  return (
    <Box sx={rootStyle}>
      <Container maxWidth="sm">
        <Card sx={cardStyle}>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <Box sx={{ mb: 3 }}>
              {getIcon()}
            </Box>

            <Typography
              variant="h4"
              gutterBottom
              sx={{ color: '#FFFFFF', fontWeight: 500 }}
            >
              {getTitle()}
            </Typography>

            <Typography
              variant="body1"
              sx={{ color: '#B3B3B3', mb: 4, px: 2 }}
            >
              {message || 'Please wait while we verify your email address...'}
            </Typography>

            {status === 'error' && (
              <Stack spacing={2} sx={{ mt: 4 }}>
                <Button
                  variant="contained"
                  startIcon={<Refresh />}
                  onClick={handleResendVerification}
                  disabled={resending}
                  sx={{
                    backgroundColor: '#00F5FF',
                    color: '#0f172a',
                    '&:hover': {
                      backgroundColor: '#00d4e6',
                    },
                    '&:disabled': {
                      backgroundColor: 'rgba(0, 245, 255, 0.5)',
                      color: '#0f172a',
                    }
                  }}
                >
                  {resending ? 'Sending...' : 'Resend Verification Email'}
                </Button>
              </Stack>
            )}

            <Stack spacing={2} sx={{ mt: 4 }}>
              <Button
                variant="outlined"
                startIcon={<Home />}
                onClick={() => navigate('/')}
                sx={{
                  borderColor: '#00F5FF',
                  color: '#00F5FF',
                  '&:hover': {
                    borderColor: '#00d4e6',
                    backgroundColor: 'rgba(0, 245, 255, 0.1)',
                  }
                }}
              >
                Go to Homepage
              </Button>

              {status === 'success' && (
                <Button
                  variant="contained"
                  onClick={() => navigate('/profile')}
                  sx={{
                    backgroundColor: '#00F5FF',
                    color: '#0f172a',
                    '&:hover': {
                      backgroundColor: '#00d4e6',
                    }
                  }}
                >
                  Go to Profile
                </Button>
              )}
            </Stack>

            {status === 'verifying' && (
              <Typography
                variant="caption"
                sx={{ color: '#64748b', mt: 4, display: 'block' }}
              >
                This may take a few moments...
              </Typography>
            )}
          </CardContent>
        </Card>

        {status === 'success' && (
          <Paper
            sx={{
              mt: 3,
              p: 2,
              background: 'rgba(74, 222, 128, 0.1)',
              border: '1px solid rgba(74, 222, 128, 0.2)',
            }}
          >
            <Typography variant="body2" sx={{ color: '#4CAF50', textAlign: 'center' }}>
              You can now access all features of the platform!
            </Typography>
          </Paper>
        )}
      </Container>
    </Box>
  );
};

export default VerifyEmail;